import type { PlaceKind } from '@bandido/shared/types';

import type { TransferPointPayload } from './queries';
import type { UrlParams } from './useSearchUrlState';
import { PlaceRef } from './places';
import { transferPointType } from './transferPointPicker';
import {
  transferPointIataFromParams,
  transferPointKindFromParams,
  transferPointKindParam,
  transferPointNameParam,
  transferPointPlaceFromParams,
} from './transfers';

/**
 * El punto de un traslado como estado del formulario, y sus tres
 * conversiones: a payload del hub, a URL y de vuelta.
 *
 * Vive en un `.ts` y no dentro de `TransfersSearchForm.tsx` porque el jest de
 * este repo NO resuelve `.tsx` a propósito (ver el docstring de
 * `PlaceAutocomplete.tsx`): mientras esto vivía en el componente, la cadena
 * completa —link guardado → estado → payload— no se podía afirmar en ningún
 * test, y es justamente donde vive la invariante del tipo del punto. El
 * componente solo cablea.
 */

export type PointMode = 'iata' | 'coords';

export interface PointState {
  mode: PointMode;
  /**
   * El tipo del punto NO es estado: se deriva del modo donde se arma el
   * payload (`transferPointType`). Guardarlo lo volvia arrastrable —de la
   * URL, de una transicion anterior— y un valor arrastrado que la
   * normalizacion no contemplara producia un payload que nuestro propio
   * borde rechaza. Sin campo no hay nada que arrastrar.
   */
  iata: string;
  /**
   * Solo aplica con `mode === 'iata'`: lugar canónico elegido en el campo
   * unificado del punto (`TransferPointAutocomplete`, maestro filtrado por
   * `TRANSFER_POINT_PLACE_KINDS`), en vez de tipear el IATA a mano (#2178).
   * `null` = no hay selección por lugar — mutuamente excluyente con `iata`
   * no vacío (mismo criterio de `resolvePlacesPickerMode`: el modo lo decide
   * cuál de los dos tiene contenido). Al buscar, se manda el `plc_*` TAL CUAL
   * (`{kind: 'place', place}`) — Bandido nunca lo traduce a IATA/coordenadas,
   * eso lo hace rutero por conector.
   */
  placePoint: PlaceRef | null;
  /**
   * El tipo REAL del lugar canónico elegido, cuando se conoce. Va aparte de
   * `placePoint.kind` a propósito: ese es display-only y se reconstruye fijo
   * en `'airport'` desde la URL, así que decidir con él rotularía una estación
   * canónica como "hora del vuelo". Ausente = no se sabe, y entonces no se
   * afirma ningún vuelo.
   */
  placeKind?: PlaceKind;
  name: string;
  address: string;
  town?: string;
  country?: string;
  zip?: string;
  lat: string;
  lng: string;
  /**
   * Solo aplica con `mode === 'coords'`: muestra los campos editables de
   * nombre/dirección/lat/lng en vez del campo unificado. Es un fallback
   * explícito: un punto resuelto o restaurado se ve como un campo elegido,
   * no como cuatro inputs técnicos.
   *
   * NUNCA viaja a la URL ni al payload: es estado de UI puro. Vive en
   * `PointState` porque "Limpiar" e intercambiar origen/destino mutan el
   * punto desde el padre sin remontar `PointFields`.
   */
  useManualFields: boolean;
}

export function toPayload(point: PointState): TransferPointPayload {
  if (point.mode === 'iata') {
    // El lugar manda si hay uno elegido: mutuamente excluyente con `iata`
    // (ver el doc de `PointState.placePoint`). Se manda el `plc_*` TAL CUAL,
    // sin `type` — rutero ya sabe qué es por el propio lugar (§1 de
    // docs/PLACES.md: Bandido nunca traduce un lugar a código de proveedor).
    if (point.placePoint) return { kind: 'place', place: point.placePoint.id };
    return {
      kind: 'detail',
      type: transferPointType('iata'),
      iata: point.iata.toUpperCase(),
    };
  }
  // Sin `type`: una coordenada que nadie clasifico no declara ninguno (ver
  // `transferPointType`). El contrato lo admite y lo prefiere al tipo fijo.
  return {
    kind: 'detail',
    name: point.name,
    address: point.address,
    ...(point.town?.trim() ? { town: point.town.trim() } : {}),
    ...(point.country?.trim() ? { country: point.country.trim() } : {}),
    ...(point.zip?.trim() ? { zip: point.zip.trim() } : {}),
    lat: Number(point.lat),
    lng: Number(point.lng),
  };
}

/** A resolved or restored GPS point can need postal data before booking. */
export function needsPostalCompletion(point: PointState): boolean {
  return point.mode === 'coords' && !point.placePoint && isPointValid(point) &&
    [point.town, point.country, point.zip].some((value) => !value?.trim());
}

export function isPointValid(point: PointState): boolean {
  if (point.mode === 'iata') {
    return Boolean(point.placePoint) || point.iata.trim().length === 3;
  }
  return (
    Boolean(point.name.trim()) &&
    Boolean(point.address.trim()) &&
    !Number.isNaN(Number(point.lat)) &&
    point.lat.trim() !== '' &&
    !Number.isNaN(Number(point.lng)) &&
    point.lng.trim() !== ''
  );
}

/** Un punto del viaje en la URL, con el prefijo del rol (`o` / `d`). */
/**
 * `${prefix}iata` es el MISMO param en los dos submodos de `mode==='iata'`
 * (mutuamente excluyentes por forma, igual que `destino` en tours/#2175):
 * un IATA de 3 letras en modo nativo, un `plc_*` en modo lugar. El nombre
 * del lugar (solo display, no viaja al payload) va en `${prefix}iataNombre`
 * — sin eso, restaurar un link mostraría el `plc_*` crudo en vez del
 * nombre.
 */
export function pointToParams(point: PointState, prefix: 'o' | 'd'): UrlParams {
  const iataValue =
    point.mode === 'iata'
      ? point.placePoint
        ? point.placePoint.id
        : point.iata.toUpperCase()
      : undefined;
  return {
    // `tipo` NO viaja: el tipo se deriva del modo, y un param que nadie lee
    // solo puede volver a pisar la invariante el dia que alguien lo lea.
    [`${prefix}iata`]: iataValue || undefined,
    [transferPointNameParam(prefix)]:
      point.mode === 'iata' && point.placePoint ? point.placePoint.name : undefined,
    // El tipo REAL del lugar: sin esto un link restaurado no puede saber si la
    // ruta toca un aeropuerto, y el rótulo de la hora depende de eso.
    [transferPointKindParam(prefix)]:
      point.mode === 'iata' && point.placePoint ? point.placeKind : undefined,
    [`${prefix}nombre`]: point.mode === 'coords' ? point.name : undefined,
    [`${prefix}dir`]: point.mode === 'coords' ? point.address : undefined,
    [`${prefix}ciudad`]: point.mode === 'coords' ? point.town : undefined,
    [`${prefix}pais`]: point.mode === 'coords' ? point.country : undefined,
    [`${prefix}cp`]: point.mode === 'coords' ? point.zip : undefined,
    [`${prefix}lat`]: point.mode === 'coords' ? point.lat : undefined,
    [`${prefix}lng`]: point.mode === 'coords' ? point.lng : undefined,
  };
}

export function pointFromParams(
  params: Record<string, string>,
  prefix: 'o' | 'd',
  fallbackMode: PointMode = 'iata',
): PointState {
  const iataParam = params[`${prefix}iata`] ?? '';
  const name = params[`${prefix}nombre`] ?? '';
  const address = params[`${prefix}dir`] ?? '';
  const lat = params[`${prefix}lat`] ?? '';
  const lng = params[`${prefix}lng`] ?? '';
  // El modo se resuelve ANTES del tipo porque el tipo sale de el: un link
  // viejo trae `tipo=H` en toda punta por coordenadas (era el valor fijo que
  // mandaba bandido, no algo que alguien haya mirado), y honrarlo
  // reintroduciria la pista falsa que #2391 saca.
  const mode: PointMode =
    iataParam ? 'iata' : name || address || lat || lng ? 'coords' : fallbackMode;
  return {
    mode,
    iata: transferPointIataFromParams(iataParam),
    placeKind: transferPointKindFromParams(params[transferPointKindParam(prefix)]),
    placePoint: transferPointPlaceFromParams(
      iataParam,
      params[transferPointNameParam(prefix)],
    ),
    name,
    address,
    town: params[`${prefix}ciudad`] ?? '',
    country: params[`${prefix}pais`] ?? '',
    zip: params[`${prefix}cp`] ?? '',
    lat,
    lng,
    // `useManualFields` NUNCA viaja en la URL. Un punto restaurado con
    // lat/lng se muestra como una dirección seleccionada en el buscador; los
    // campos manuales quedan solo para fallback explícito o setting apagado.
    useManualFields: false,
  };
}
